import { useCallback, type SyntheticEvent } from 'react'
import type { AdminAccount, AdminProfile } from '../../types'
import { API_BASE } from './constants'
import { fetchWithPolicy } from '../../lib/api/fetchPolicy'
import type { UseAdminAuthParams } from './useAdminAuth.types'
import { parseAdminLoginPayload, handleAdminLoginFailure } from './useAdminLogin.response'
import { applyAdminPostLoginRouting } from './useAdminLogin.routing'
import { setAuthToken as setRuntimeAuthToken } from '../../lib/api/authToken'
import { bootstrapAdminCsrfToken, resolveCsrfHeaderForMethod } from '../../lib/api/csrfToken'

type LoginParams = Pick<
  UseAdminAuthParams,
  | 't'
  | 'loginForm'
  | 'loginStage'
  | 'setLoginForm'
  | 'setAuthLoading'
  | 'setToken'
  | 'setLoginStage'
  | 'setRequiresDeviceTrust'
  | 'setSection'
  | 'resolveSectionFromUrl'
  | 'hydrateProfile'
  | 'bootstrapSession'
>

type AdminCredentialsLoginInput = {
  username: string
  password: string
  botProtectionProviderId: string
  botProtectionToken: string
}

export const buildAdminCredentialsLoginPayload = ({
  username,
  password,
  botProtectionProviderId,
  botProtectionToken,
}: AdminCredentialsLoginInput) => ({
  username,
  password,
  botProtection: botProtectionProviderId.trim()
    ? {
        providerId: botProtectionProviderId.trim(),
        token: botProtectionToken.trim(),
      }
    : undefined,
})

export const useAdminLogin = ({
  t,
  loginForm,
  loginStage,
  setLoginForm,
  setAuthLoading,
  setToken,
  setLoginStage,
  setRequiresDeviceTrust,
  setSection,
  resolveSectionFromUrl,
  hydrateProfile,
  bootstrapSession,
}: LoginParams) =>
  useCallback(
    async (event: SyntheticEvent<HTMLFormElement>) => {
      event.preventDefault()
      setAuthLoading(true)
      try {
        const isOtpStage = loginStage === 'otp'
        const sessionId = loginForm.twoFactorSessionId?.trim() ?? ''
        if (isOtpStage && !sessionId) {
          const invalidSessionError = new Error(t('auth.passkey.error.session'))
          ;(invalidSessionError as Error & { code?: string }).code = 'INVALID_2FA_SESSION'
          throw invalidSessionError
        }
        const response = await fetchWithPolicy({
          request: isOtpStage ? `${API_BASE}/auth/admin/2fa/verify` : `${API_BASE}/auth/admin/login`,
          init: {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(
              isOtpStage
                ? {
                    sessionId,
                    code: loginForm.otp?.trim() ?? '',
                  }
                : buildAdminCredentialsLoginPayload({
                    username: loginForm.username,
                    password: loginForm.password,
                    botProtectionProviderId: loginForm.botProtectionProviderId ?? '',
                    botProtectionToken: loginForm.botProtectionToken ?? '',
                  }),
            ),
          },
        })

        const raw = await response.text()
        const data = parseAdminLoginPayload(raw)
        const retryAfterRaw = response.headers.get('retry-after')
        const retryAfterSeconds =
          retryAfterRaw && /^\d+$/.test(retryAfterRaw.trim()) ? Number(retryAfterRaw.trim()) : null

        if (!response.ok) {
          return handleAdminLoginFailure({
            responseStatus: response.status,
            payload: data,
            raw,
            t,
            retryAfterSeconds,
            setLoginStage,
            setLoginForm,
          })
        }

        const parsed = data ?? {}
        if (parsed.requiresTwoFactor === true) {
          const twoFactorSessionId = typeof parsed.sessionId === 'string' ? parsed.sessionId.trim() : ''
          if (!twoFactorSessionId) {
            throw new Error(t('auth.error.generic'))
          }
          setLoginStage('otp')
          setLoginForm((prev) => ({
            ...prev,
            otp: '',
            botProtectionToken: '',
            twoFactorSessionId,
          }))
          return { authenticated: false as const, otpRequired: true as const }
        }

        let tokenValue = (parsed.token as string | undefined) ?? null
        if (!tokenValue) {
          await bootstrapAdminCsrfToken(API_BASE)
          const validateResponse = await fetchWithPolicy({
            request: `${API_BASE}/auth/validate`,
            init: {
              method: 'POST',
              credentials: 'include',
              headers: resolveCsrfHeaderForMethod('POST'),
            },
            retries: 0,
          })
          if (validateResponse.ok) {
            const validatePayload = (await validateResponse.json()) as { token?: string } | null
            const validateToken = typeof validatePayload?.token === 'string' ? validatePayload.token.trim() : ''
            tokenValue = validateToken || null
          }
        }
        if (!tokenValue) {
          const missingTokenError = new Error('AUTH_REQUIRED')
          ;(missingTokenError as Error & { code?: string }).code = 'AUTH_REQUIRED'
          throw missingTokenError
        }
        setToken(tokenValue)
        setRuntimeAuthToken(tokenValue)
        await bootstrapAdminCsrfToken(API_BASE)
        setLoginStage('credentials')
        setLoginForm({
          username: '',
          password: '',
          otp: '',
          botProtectionToken: '',
          botProtectionProviderId: '',
          twoFactorSessionId: '',
        })

        if (parsed.profile || parsed.account || parsed.user) {
          hydrateProfile(
            (parsed.profile as Record<string, unknown> | undefined) as Partial<AdminProfile>,
            ((parsed.account ?? parsed.user) as AdminAccount) ?? null,
          )
        }
        if (parsed.session) {
          bootstrapSession(parsed.session)
        }

        const needsTrust = Boolean(parsed.requiresDeviceTrust)
        setRequiresDeviceTrust(needsTrust)
        if (!needsTrust) {
          const account = ((parsed.account ?? parsed.user) as AdminAccount | null) ?? null
          applyAdminPostLoginRouting({
            account,
            setSection,
            resolveSectionFromUrl,
          })
        }
        return { authenticated: true as const, otpRequired: false as const }
      } finally {
        setAuthLoading(false)
      }
    },
    [
      bootstrapSession,
      hydrateProfile,
      loginForm,
      loginStage,
      resolveSectionFromUrl,
      setAuthLoading,
      setLoginForm,
      setLoginStage,
      setRequiresDeviceTrust,
      setSection,
      setToken,
      t,
    ],
  )
